import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, UserRound, Target, ListChecks, Sparkles,
  GraduationCap, Plane, Stethoscope, Wallet, MonitorSmartphone,
  Building2, HeartHandshake, FileText, Settings, LogOut, ChevronsLeft, ChevronsRight,
  Zap, Receipt, IndianRupee, ShieldCheck, Shield, UserPlus, ClipboardCheck, IdCard,
  BookOpen, FileBarChart, Calculator,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

const ADMIN_ROLES = ["super_admin", "founder", "hr_admin"];
// Scoped roles see only their own records — hide Finance overview, HR and
// cross-team Reports from the sidebar for them.
const SCOPED_ROLES = [
  "employee", "academy_counsellor", "study_abroad_counsellor",
  "wealth_executive", "digital_pm",
];
// These roles can still see per-record quotations/invoices they created,
// but no cross-team roll-ups. Ops manager is EXCLUDED — Phase 6.3.
const FINANCE_ROLES = ["super_admin", "founder", "hr_admin", "finance"];
const PARTNER_ROLES = ["external_partner", "franchise_partner"];

const NAV = [
  { section: "Core", items: [
    { to: "/",             label: "Dashboard",     icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/leads",        label: "Leads",         icon: Target,          testid: "nav-leads" },
    { to: "/contacts",     label: "Contacts",      icon: UserRound,       testid: "nav-contacts" },
    { to: "/opportunities",label: "Opportunities", icon: Sparkles,        testid: "nav-opportunities" },
    { to: "/tasks",        label: "Tasks",         icon: ListChecks,      testid: "nav-tasks" },
    { to: "/my-id",        label: "My Digital ID", icon: IdCard,          testid: "nav-my-id" },
  ]},
  { section: "Divisions", items: [
    { to: "/divisions/academy",      label: "Academy",       icon: GraduationCap,      testid: "nav-academy" },
    { to: "/divisions/study-abroad", label: "Study Abroad",  icon: Plane,              testid: "nav-study-abroad" },
    { to: "/divisions/mbbs",         label: "MBBS Abroad",   icon: Stethoscope,        testid: "nav-mbbs" },
    { to: "/divisions/wealth",       label: "Wealth",        icon: Wallet,             testid: "nav-wealth" },
    { to: "/divisions/insurance",    label: "Insurance",     icon: Shield,             testid: "nav-insurance" },
    { to: "/divisions/digital",      label: "Digital",       icon: MonitorSmartphone,  testid: "nav-digital" },
  ]},
  { section: "Finance", financeOnly: true, items: [
    { to: "/finance/quotations",     label: "Quotations",       icon: FileText,    testid: "nav-quotations" },
    { to: "/finance/invoices",       label: "Invoices",         icon: Receipt,     testid: "nav-invoices" },
    { to: "/finance/commissions",    label: "Commissions",      icon: IndianRupee, testid: "nav-commissions" },
    { to: "/admin/partner-earnings", label: "Partner Earnings", icon: Wallet,      testid: "nav-partner-earnings" },
  ]},
  { section: "My Portal", partnerOnly: true, items: [
    { to: "/my-earnings",            label: "My Earnings",      icon: Wallet,      testid: "nav-my-earnings" },
    { to: "/finance/commissions",    label: "Earnings Calculator", icon: Calculator, testid: "nav-partner-calculator" },
  ]},
  { section: "People & Ops", managerOnly: true, items: [
    { to: "/hr",          label: "HR",          icon: Users,          testid: "nav-hr" },
    { to: "/franchise",   label: "Franchise",   icon: Building2,      testid: "nav-franchise" },
    { to: "/partners",    label: "Partners",    icon: HeartHandshake, testid: "nav-partners" },
    { to: "/automations", label: "Automations", icon: Zap,            testid: "nav-automations" },
    { to: "/reports",     label: "Reports",     icon: FileText,       testid: "nav-reports" },
  ]},
  { section: "Admin", adminOnly: true, items: [
    { to: "/admin/approvals",       label: "Approval Centre", icon: ClipboardCheck, testid: "nav-admin-approvals" },
    { to: "/admin/users",           label: "Users & Access",   icon: ShieldCheck,  testid: "nav-admin-users" },
    { to: "/admin/access-requests", label: "Access Requests",  icon: UserPlus,     testid: "nav-admin-access-requests" },
    { to: "/admin/partner-payments", label: "Partner Payments", icon: IndianRupee, testid: "nav-admin-partner-payments" },
    { to: "/admin/payment-settings", label: "Payment Settings", icon: Shield,      testid: "nav-admin-payment-settings" },
  ]},
];

export default function Sidebar({ collapsed, onToggle }) {
  const { user, logout } = useAuth();
  const loc = useLocation();

  return (
    <aside
      data-testid="app-sidebar"
      className="h-screen sticky top-0 flex flex-col text-white"
      style={{
        background: "var(--miro-navy)",
        width: collapsed ? "72px" : "260px",
        transition: "width 240ms ease",
      }}
    >
      <div className="flex items-center justify-between px-4 py-5 border-b border-white/10">
        <div className="flex items-center gap-2 overflow-hidden">
          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center flex-shrink-0 p-1">
            <img src="/miro-logo.png" alt="MIRO" className="w-full h-full object-contain"/>
          </div>
          {!collapsed && (
            <div className="leading-tight">
              <div className="font-display font-black text-lg tracking-tight">MIRO One</div>
              <div className="text-[10px] uppercase tracking-widest text-white/50">Learn. Earn. Grow.</div>
            </div>
          )}
        </div>
        <button
          data-testid="sidebar-toggle-btn"
          onClick={onToggle}
          className="text-white/60 hover:text-white transition-colors"
          aria-label="Toggle sidebar"
        >
          {collapsed ? <ChevronsRight size={18}/> : <ChevronsLeft size={18}/>}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
        {NAV.map((sec) => {
          if (sec.adminOnly && !ADMIN_ROLES.includes(user?.role)) return null;
          if (sec.financeOnly && !FINANCE_ROLES.includes(user?.role)) return null;
          if (sec.partnerOnly && !PARTNER_ROLES.includes(user?.role)) return null;
          if (sec.managerOnly && SCOPED_ROLES.includes(user?.role)) return null;
          // External / franchise partners and student/client users see ONLY their
          // own portal + dashboard — never the full CRM navigation.
          const isPartnerOrClient = PARTNER_ROLES.includes(user?.role)
                                    || user?.role === "student_client";
          if (isPartnerOrClient && !sec.partnerOnly && sec.section !== "Core") {
            return null;
          }
          if (sec.managerOnly && isPartnerOrClient) return null;
          // Trim Core to Dashboard + My Digital ID only for partners/clients.
          let items = sec.items;
          if (isPartnerOrClient && sec.section === "Core") {
            items = sec.items.filter(i => i.to === "/" || i.to === "/my-id");
          }
          return (
          <div key={sec.section}>
            {!collapsed && (
              <div className="miro-overline px-2 mb-2 text-white/40">{sec.section}</div>
            )}
            <div className="space-y-1">
              {items.map((item) => {
                const Icon = item.icon;
                const active = loc.pathname === item.to || (item.to !== "/" && loc.pathname.startsWith(item.to));
                return (
                  <NavLink
                    key={item.to}
                    to={item.soon ? "#" : item.to}
                    data-testid={item.testid}
                    onClick={(e) => { if (item.soon) e.preventDefault(); }}
                    className={`miro-nav-item ${active ? "active" : ""} ${item.soon ? "opacity-60" : ""}`}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon size={18} className="flex-shrink-0"/>
                    {!collapsed && (
                      <>
                        <span className="flex-1 truncate">{item.label}</span>
                        {item.soon && (
                          <span className="text-[10px] uppercase tracking-wider text-white/40">soon</span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
        );})}
      </nav>

      <div className="border-t border-white/10 p-3">
        <a
          data-testid="download-user-manual"
          href="/MIRO_One_User_Manual.pdf"
          download="MIRO_One_User_Manual.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="miro-nav-item mb-2"
          title={collapsed ? "Download User Manual" : undefined}
        >
          <BookOpen size={18} className="flex-shrink-0"/>
          {!collapsed && (
            <span className="flex-1 truncate">User Manual</span>
          )}
        </a>
        {ADMIN_ROLES.includes(user?.role) && (
          <a
            data-testid="download-system-report"
            href="/MIRO_One_System_Report.pdf"
            download="MIRO_One_System_Report.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="miro-nav-item mb-2"
            title={collapsed ? "Download System Report (Admin)" : undefined}
          >
            <FileBarChart size={18} className="flex-shrink-0"/>
            {!collapsed && (
              <span className="flex-1 truncate">System Report</span>
            )}
          </a>
        )}
        {!collapsed ? (
          <div className="flex items-center gap-3 px-2">
            <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center font-display font-bold">
              {user?.name?.[0] || "M"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[11px] text-white/50 truncate">{user?.role?.replace(/_/g," ")}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="text-white/60 hover:text-white transition-colors"
              title="Log out"
            >
              <LogOut size={17}/>
            </button>
          </div>
        ) : (
          <button
            data-testid="logout-btn-collapsed"
            onClick={logout}
            className="w-full flex justify-center text-white/60 hover:text-white transition-colors"
            title="Log out"
          >
            <LogOut size={18}/>
          </button>
        )}
      </div>
    </aside>
  );
}
